import React, { useEffect, useState } from 'react';
import { Cloud, CloudFog, CloudLightning, CloudRain, CloudSnow, Sun, Wind } from 'lucide-react';

interface WeatherData {
    current: {
        temp: number;
        weatherCode: number;
    };
    daily: {
        time: string[];
        weatherCode: number[];
        tempMax: number[];
        tempMin: number[];
    };
    city: string;
}

// Mapa de códigos WMO para descrições e ícones
const getWeatherInfo = (code: number) => {
    if (code === 0) return { label: 'Céu Limpo', icon: <Sun className="w-full h-full text-yellow-400" /> };
    if (code >= 1 && code <= 3) return { label: 'Parcialmente Nublado', icon: <Cloud className="w-full h-full text-gray-200" /> };
    if (code >= 45 && code <= 48) return { label: 'Neblina', icon: <CloudFog className="w-full h-full text-gray-400" /> };
    if (code >= 51 && code <= 67) return { label: 'Chuva', icon: <CloudRain className="w-full h-full text-blue-300" /> };
    if (code >= 71 && code <= 77) return { label: 'Neve', icon: <CloudSnow className="w-full h-full text-white" /> };
    if (code >= 80 && code <= 82) return { label: 'Pancadas de Chuva', icon: <CloudRain className="w-full h-full text-blue-400" /> };
    if (code >= 95 && code <= 99) return { label: 'Tempestade', icon: <CloudLightning className="w-full h-full text-purple-400" /> };
    return { label: 'Nublado', icon: <Cloud className="w-full h-full text-gray-300" /> };
};

const getDayName = (dateStr: string) => {
    const days = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
    return days[new Date(dateStr).getDay()];
};

const WeatherOverlay = ({ locationOverride }: { locationOverride?: string }) => {
    const [weather, setWeather] = useState<WeatherData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [debugInfo, setDebugInfo] = useState<any>(null);

    useEffect(() => {
        // Se houver um override de local, usar busca direta em vez de GPS
        if (locationOverride) {
            const fetchManualLocation = async () => {
                try {
                    console.log(`📍 [WEATHER] Usando local fixo: ${locationOverride}`);
                    // Buscar coordenadas do nome da cidade
                    const searchRes = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationOverride)}`, {
                        headers: { 'User-Agent': 'WatchPlayer/1.0' }
                    });
                    const searchData = await searchRes.json();

                    if (searchData && searchData.length > 0) {
                        const { lat, lon, display_name } = searchData[0];
                        console.log('📍 [WEATHER] Local fixo encontrado:', lat, lon);

                        // Buscar dados meteorológicos com as coords obtidas
                        const weatherRes = await fetch(
                            `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min&timezone=auto`
                        );
                        const weatherData = await weatherRes.json();

                        setWeather({
                            current: {
                                temp: Math.round(weatherData.current.temperature_2m),
                                weatherCode: weatherData.current.weather_code,
                            },
                            daily: {
                                time: weatherData.daily.time,
                                weatherCode: weatherData.daily.weather_code,
                                tempMax: weatherData.daily.temperature_2m_max,
                                tempMin: weatherData.daily.temperature_2m_min,
                            },
                            city: locationOverride, // Usar o nome fornecido ou o retornado pelo Nominatim
                        });
                        setDebugInfo({
                            coords: { latitude: parseFloat(lat), longitude: parseFloat(lon) },
                            source: `Mural Override: ${locationOverride}`,
                            rawAddress: searchData[0]
                        });
                        setLoading(false);
                        return;
                    }
                } catch (e) {
                    console.error('Erro ao buscar local fixo', e);
                }
                // Fallback para GPS se falhar
            };
            fetchManualLocation();
            return;
        }

        if (!navigator.geolocation) {
            setError('Geolocalização não suportada');
            setLoading(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const { latitude, longitude } = position.coords;

                    // Buscar dados meteorológicos
                    const weatherRes = await fetch(
                        `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min&timezone=auto`
                    );
                    const weatherData = await weatherRes.json();

                    // Tentar obter nome da cidade (reversa)
                    let city = 'Sua Localização';
                    try {
                        console.log('📍 [WEATHER] Buscando cidade para lat/lon:', latitude, longitude);

                        // 1. BigDataCloud (Rápido, sem limites estritos)
                        const cityRes = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=pt`);
                        const cityData = await cityRes.json();
                        console.log('📍 [WEATHER] Dados de cidade recebidos (BigDataCloud):', cityData);

                        // 2. OpenStreetMap Nominatim (Mais preciso para Brasil, usado para comparação/confirmação)
                        try {
                            const nominatimRes = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`, {
                                headers: { 'User-Agent': 'WatchPlayer/1.0' }
                            });
                            if (nominatimRes.ok) {
                                const nominatimData = await nominatimRes.json();
                                console.log('📍 [WEATHER] Comparativo - Dados Nominatim:', nominatimData);

                                // Opcional: Se quiser priorizar Nominatim que costuma ser mais preciso
                                const nomCity = nominatimData.address?.city || nominatimData.address?.town || nominatimData.address?.municipality;
                                if (nomCity) {
                                    console.log('📍 [WEATHER] Usando cidade do Nominatim por ser mais preciso:', nomCity);
                                    city = nomCity;
                                    // Pular o resto se já temos uma cidade confiável
                                    setWeather({
                                        current: {
                                            temp: Math.round(weatherData.current.temperature_2m),
                                            weatherCode: weatherData.current.weather_code,
                                        },
                                        daily: {
                                            time: weatherData.daily.time,
                                            weatherCode: weatherData.daily.weather_code,
                                            tempMax: weatherData.daily.temperature_2m_max,
                                            tempMin: weatherData.daily.temperature_2m_min,
                                        },
                                        city: nomCity,
                                    });
                                    setDebugInfo({
                                        coords: { latitude, longitude },
                                        source: 'Nominatim (High Precision)',
                                        rawAddress: nominatimData
                                    });
                                    setLoading(false);
                                    return;
                                }
                            }
                        } catch (nomErr) {
                            console.warn('Erro ao consultar Nominatim:', nomErr);
                        }

                        // Tentar extrair a cidade de várias propriedades do BigDataCloud (fallback)
                        city = cityData.city || cityData.locality || cityData.principalSubdivision || 'Local Desconhecido';
                    } catch (e) {
                        console.warn('Erro ao buscar nome da cidade', e);
                    }

                    setWeather({
                        current: {
                            temp: Math.round(weatherData.current.temperature_2m),
                            weatherCode: weatherData.current.weather_code,
                        },
                        daily: {
                            time: weatherData.daily.time,
                            weatherCode: weatherData.daily.weather_code,
                            tempMax: weatherData.daily.temperature_2m_max,
                            tempMin: weatherData.daily.temperature_2m_min,
                        },
                        city,
                    });
                    setDebugInfo({
                        coords: { latitude, longitude },
                        source: 'Fallback/BigDataCloud',
                        rawAddress: cityData || { error: 'No data' }
                    });
                } catch (err) {
                    console.error(err);
                    setError('Erro ao carregar previsão');
                } finally {
                    setLoading(false);
                }
            },
            (err) => {
                console.error(err);
                setError('Acesso à localização negado');
                setLoading(false);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    }, []);

    if (loading) return null; // Ou um spinner bonito
    if (error || !weather) return null;

    const currentInfo = getWeatherInfo(weather.current.weatherCode);
    const today = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });

    return (
        <div className="absolute inset-0 z-[9999] flex flex-col items-center justify-between p-8 text-white overflow-hidden animate-in fade-in zoom-in duration-500">
            {/* Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-400 to-blue-800 -z-10" />

            {/* DEBUG INFO OVERLAY */}
            <div className="absolute bottom-4 right-4 bg-black/50 p-4 rounded text-xs text-white max-w-md pointer-events-none z-50">
                <p className="font-bold mb-1">🔍 DEBUG INFO:</p>
                {debugInfo && (
                    <>
                        <p>Lat/Lon: {debugInfo.coords.latitude.toFixed(5)}, {debugInfo.coords.longitude.toFixed(5)}</p>
                        <p>Source used: {debugInfo.source}</p>
                        <p className="truncate">Raw City/Data: {JSON.stringify(debugInfo.rawAddress, null, 2)}</p>
                    </>
                )}
            </div>

            {/* City and Date Header */}
            <div className="w-full flex justify-between items-start mt-4">
                <div>
                    <h1 className="text-6xl font-bold drop-shadow-lg mb-2">{weather.city}</h1>
                    <p className="text-3xl font-light opacity-90 capitalize">{today}</p>
                </div>
                <div className="flex items-center">
                    {/* Você pode adicionar um relógio aqui se quiser */}
                </div>
            </div>

            {/* Main Weather Display */}
            <div className="flex flex-col items-center justify-center flex-1 w-full">
                <div className="flex items-center justify-center gap-12">
                    <div className="w-64 h-64 drop-shadow-2xl filter">
                        {currentInfo.icon}
                    </div>
                    <div className="text-[12rem] leading-none font-thin drop-shadow-xl tracking-tighter">
                        {weather.current.temp}°
                    </div>
                </div>
                <p className="text-4xl font-medium mt-4 opacity-90 tracking-wide">{currentInfo.label}</p>
            </div>

            {/* Daily Forecast Cards */}
            <div className="w-full grid grid-cols-6 gap-4 mb-4">
                {weather.daily.time.slice(1, 7).map((date, index) => {
                    const code = weather.daily.weatherCode[index + 1]; // +1 para pular hoje
                    const max = Math.round(weather.daily.tempMax[index + 1]);
                    const min = Math.round(weather.daily.tempMin[index + 1]);
                    const info = getWeatherInfo(code);

                    return (
                        <div key={date} className="bg-white/10 backdrop-blur-md rounded-2xl p-4 flex flex-col items-center justify-center border border-white/20 shadow-lg">
                            <span className="text-lg font-bold mb-2">{getDayName(date)}</span>
                            <div className="w-12 h-12 my-2">
                                {info.icon}
                            </div>
                            <div className="flex gap-2 text-xl font-medium mt-1">
                                <span className="text-white">{max}°</span>
                                <span className="text-blue-200">{min}°</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default WeatherOverlay;
